import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { MoreVerticalIcon, SquareArrowUpRightIcon, Delete02Icon, FolderTransferIcon } from '@hugeicons/core-free-icons';
import { toast } from 'sonner';
import { useIntegrations } from '../../hooks/useIntegrations';
import { useExportDialog } from '../../lib/exportDialog';
import { useAuth } from '../../lib/auth';
import { EXPORT_PROVIDERS, getExportProviderLabel, type ExportProvider, toUserFacingMessage } from '@overlayqa/shared';
import { log } from '@/lib/logger';

interface IssueActionsProps {
  issueId: string;
  onDelete: () => void;
  isDeleting: boolean;
  /**
   * Opens the move-to-project picker. Optional so the existing test renders and
   * any caller that has not wired the dialog yet simply does not show the item,
   * rather than showing one that does nothing.
   */
  onMoveClick?: () => void;
}

/**
 * Dropdown menu for issue export, status transitions, and delete.
 * Styled to match extension's oq-dropdown pattern. Shows ALL enabled
 * providers in a labeled "Export" group even when the user hasn't connected
 * them yet — unconnected providers are DISABLED with a connect-hint tooltip
 * instead of rendering a "Connect X" item that navigates away (user
 * decision 2026-07-04).
 */
export function IssueActions({
  issueId,
  onDelete,
  isDeleting,
  onMoveClick,
}: IssueActionsProps) {
  const exportWithDialog = useExportDialog();
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { data: integrations } = useIntegrations();
  const { getToken } = useAuth();

  const connectedSet = new Set((integrations ?? []).map((i) => i.provider));

  const handleExport = useCallback(async (provider: ExportProvider) => {
    log.info('[IssueActions] Export click', { provider, connected: connectedSet.has(provider) });

    if (!connectedSet.has(provider)) {
      toast.info(`Connect ${getExportProviderLabel(provider)} in Integrations to export issues.`);
      navigate('/integrations');
      setIsOpen(false);
      return;
    }

    setIsExporting(true);
    try {
      const token = await getToken();
      // Every provider opens the shared destination chooser, the same one the
      // Issues table's bulk action and the extension open: the user picks the
      // project / team / workspace / board there. Until 2026-10-22 five
      // providers were resolved to the FIRST destination the account could see
      // and exported there with no picker (board P1, 2026-10-22).
      await exportWithDialog(provider, token ?? undefined, [issueId]);
    } catch (err) {
      // Loading the issue (or a missing session) throws before the chooser
      // opens, so without this catch that was an unhandled rejection with zero
      // UI feedback. Inside the chooser, failures render in place.
      log.error('[IssueActions] Export failed', {
        provider,
        error: err instanceof Error ? err.message : String(err),
      });
      const message = toUserFacingMessage(err, '');
      toast.error(message || `Failed to export to ${getExportProviderLabel(provider)}.`);
    } finally {
      setIsExporting(false);
      setIsOpen(false);
    }
  }, [issueId, getToken, navigate, connectedSet, exportWithDialog]);

  log.info('[IssueActions] Rendering dropdown');

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:border-gray-400 transition-colors"
        aria-label="Issue actions"
      >
        <HugeiconsIcon icon={MoreVerticalIcon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4" />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 z-20 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
          <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            Export
          </div>
          {EXPORT_PROVIDERS.map((provider) => {
            const connected = connectedSet.has(provider);
            return (
              <button
                key={provider}
                type="button"
                className={`flex items-center gap-2 w-full px-3 py-2 text-xs whitespace-nowrap transition-colors ${
                  connected ? 'text-gray-700 hover:bg-gray-50' : 'text-gray-300 cursor-not-allowed'
                }`}
                onClick={() => handleExport(provider)}
                disabled={!connected || isExporting}
                title={connected ? undefined : `Connect ${getExportProviderLabel(provider)} in Integrations to export issues.`}
              >
                <HugeiconsIcon
                  icon={SquareArrowUpRightIcon}
                  size={14}
                  strokeWidth={2}
                  color="currentColor"
                  className={`w-3.5 h-3.5 ${connected ? 'text-gray-400' : 'text-gray-300'}`}
                />
                <span className="text-cap-trim">{isExporting && connected ? 'Exporting...' : `Export to ${getExportProviderLabel(provider)}`}</span>
              </button>
            );
          })}
          {onMoveClick && (
            <>
              <div className="border-t border-gray-100 my-1" />
              <button
                type="button"
                // Plain, not red: moving an issue is reversible and must not
                // share the visual class of the one action that is not.
                className="flex items-center gap-2 w-full px-3 py-2 text-xs whitespace-nowrap text-gray-700 hover:bg-gray-50 transition-colors"
                onClick={() => { onMoveClick(); setIsOpen(false); }}
              >
                <HugeiconsIcon icon={FolderTransferIcon} size={14} strokeWidth={2} color="currentColor" className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-cap-trim">Move to project…</span>
              </button>
            </>
          )}
          <div className="border-t border-gray-100 my-1" />
          <button
            type="button"
            className="flex items-center gap-2 w-full px-3 py-2 text-xs whitespace-nowrap text-red-600 hover:bg-red-50 transition-colors"
            onClick={() => { onDelete(); setIsOpen(false); }}
            disabled={isDeleting}
          >
            <HugeiconsIcon icon={Delete02Icon} size={14} strokeWidth={2} color="currentColor" className="w-3.5 h-3.5" />
            <span className="text-cap-trim">{isDeleting ? 'Deleting...' : 'Delete issue'}</span>
          </button>
        </div>
      )}
    </div>
  );
}

/** @deprecated Use IssueActions */
export const TicketActions = IssueActions;
